/**
 * `CacheStore` over IndexedDB, in the existing key-value store.
 *
 * Not a new object store: adding one means bumping the database version, and a version bump
 * blocks until every tab still holding the old version closes — for a scorer with the app open
 * in another tab, that is a database that will not open at all. The key-value store already
 * holds the current-match pointer and the identity hint, so cached reads take keys under
 * `cache:` there. A cursor over that prefix is all `all()` needs.
 */
import { CURRENT, asPromise, committed, database } from "../storage/db.ts";
import type { CacheEntry, CacheStore } from "./readCache.ts";

/** Every key beginning `cache:` — `;` is the character after `:`. */
const RANGE = (): IDBKeyRange => IDBKeyRange.bound("cache:", "cache;", false, true);

export class IdbCacheStore implements CacheStore {
  async get(key: string): Promise<CacheEntry | undefined> {
    const db = await database();
    const tx = db.transaction(CURRENT, "readonly");
    const found = await asPromise<CacheEntry | undefined>(tx.objectStore(CURRENT).get(key));
    await committed(tx);
    return found;
  }

  async put(key: string, entry: CacheEntry): Promise<void> {
    const db = await database();
    const tx = db.transaction(CURRENT, "readwrite");
    tx.objectStore(CURRENT).put(entry, key);
    await committed(tx);
  }

  async delete(key: string): Promise<void> {
    const db = await database();
    const tx = db.transaction(CURRENT, "readwrite");
    tx.objectStore(CURRENT).delete(key);
    await committed(tx);
  }

  async all(): Promise<readonly { key: string; entry: CacheEntry }[]> {
    const db = await database();
    const tx = db.transaction(CURRENT, "readonly");
    const store = tx.objectStore(CURRENT);
    const [keys, entries] = await Promise.all([
      asPromise<IDBValidKey[]>(store.getAllKeys(RANGE())),
      asPromise<CacheEntry[]>(store.getAll(RANGE())),
    ]);
    await committed(tx);
    return keys.map((key, index) => ({ key: String(key), entry: entries[index] as CacheEntry }));
  }
}
